import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { naira, API, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import AdminSidebar from "@/components/admin/AdminSidebar";
import RevenueSummary from "@/components/admin/RevenueSummary";
import FilmManagement from "@/components/admin/FilmManagement";
import TrailerPreview from "@/components/admin/TrailerPreview";
import FilmmakerOverview from "@/components/admin/FilmmakerOverview";
import ViewerAnalytics from "@/components/admin/ViewerAnalytics";
import GeoInsights from "@/components/admin/GeoInsights";
import BroadcastEmail from "@/components/admin/BroadcastEmail";
import DonationsOverview from "@/components/admin/DonationsOverview";
import WatchPartyOverview from "@/components/admin/WatchPartyOverview";
import WatchPartyPackagesEditor from "@/components/admin/WatchPartyPackagesEditor";
import EventOverview from "@/components/admin/EventOverview";
import { MetricCard, SectionHeading } from "@/components/admin/ui";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Film, Users, ShoppingBag, Wallet, CheckCircle2, XCircle, Star, Sparkles, DollarSign, Play, Trash2, RotateCcw, AlertTriangle, Pencil, Upload, Loader2, Image as ImageIcon, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [section, setSection] = useState("overview");
  const [pending, setPending] = useState([]);
  const [allFilms, setAllFilms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [spotlight, setSpotlight] = useState({ month: "", films: [] });
  const [reviewFilm, setReviewFilm] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [aiScore, setAiScore] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [payoutModal, setPayoutModal] = useState(null);
  const [approving, setApproving] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [filmmakers, setFilmmakers] = useState([]);
  const [filmPerf, setFilmPerf] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [eventRegs, setEventRegs] = useState({ cap: 200, seats_taken: 0, registrations: [] });
  const [submissions, setSubmissions] = useState({ count: 0, submissions: [] });
  const [posts, setPosts] = useState([]);
  const [watchParties, setWatchParties] = useState([]);
  // Donation roll-up (gross + platform 30% + filmmaker 70% + recent list).
  // Fetched from /admin/donations/stats and surfaced on the overview KPI
  // grid plus the donations breakdown panel below the stats grid.
  const [donationStats, setDonationStats] = useState({ count: 0, gross_total_naira: 0, platform_share_naira: 0, creator_share_naira: 0, recent: [] });
  const [postForm, setPostForm] = useState({ title: "", body: "", excerpt: "", publish_now: true, notify_subscribers: true });
  const [postImage, setPostImage] = useState(null);
  const [postEditing, setPostEditing] = useState(null); // slug being edited (null = new)
  const [postBusy, setPostBusy] = useState(false);

  const loadPosts = async () => {
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data || []);
    } catch (err) {
      console.warn("[admin] loadPosts failed", err?.response?.status);
    }
  };

  const resetPostForm = () => {
    setPostForm({ title: "", body: "", excerpt: "", publish_now: true, notify_subscribers: true });
    setPostImage(null);
    setPostEditing(null);
  };

  const submitPost = async (e) => {
    e.preventDefault();
    if (!postForm.title.trim() || !postForm.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setPostBusy(true);
    const fd = new FormData();
    fd.append("title", postForm.title);
    fd.append("body", postForm.body);
    if (postForm.excerpt) fd.append("excerpt", postForm.excerpt);
    if (postImage) fd.append("image", postImage);
    try {
      if (postEditing) {
        fd.append("status", postForm.publish_now ? "published" : "draft");
        fd.append("notify_subscribers", postForm.notify_subscribers ? "true" : "false");
        await api.put(`/admin/posts/${postEditing}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(postForm.notify_subscribers && postForm.publish_now ? "Post updated & subscribers being notified" : "Post updated");
      } else {
        fd.append("publish_now", postForm.publish_now ? "true" : "false");
        fd.append("notify_subscribers", postForm.notify_subscribers ? "true" : "false");
        await api.post("/admin/posts", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (postForm.publish_now) {
          toast.success(postForm.notify_subscribers ? "Post published & subscribers being notified" : "Post published");
        } else {
          toast.success("Draft saved");
        }
      }
      resetPostForm();
      loadPosts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setPostBusy(false);
    }
  };

  const startEditPost = (p) => {
    setPostEditing(p.slug);
    // Editing an existing post: default to NOT re-notifying subscribers, so
    // re-saving a typo fix doesn't re-broadcast. The admin can opt in by
    // ticking the box if they want a deliberate re-send.
    setPostForm({ title: p.title, body: p.body, excerpt: p.excerpt || "", publish_now: p.status === "published", notify_subscribers: false });
    setPostImage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${p.slug}`);
      toast.success("Post deleted");
      loadPosts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const refresh = async () => {
    try {
      const [s, p, a, r, po, sp, fm, fp, vw, evt, sub, wp, don, ov] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/films/pending"),
        api.get("/admin/films"),
        api.get("/admin/festival-requests"),
        api.get("/admin/payouts"),
        api.get("/films/spotlight"),
        api.get("/admin/filmmakers"),
        api.get("/admin/films/performance"),
        api.get("/admin/viewers"),
        api.get("/admin/event/registrations").catch(() => ({ data: null })),
        api.get("/admin/submissions").catch(() => ({ data: null })),
        api.get("/admin/watch-parties").catch(() => ({ data: [] })),
        api.get("/admin/donations/stats").catch(() => ({ data: null })),
        api.get("/admin/overview").catch(() => ({ data: null })),
      ]);
      setOverview(ov.data);
      setStats(s.data);
      setPending(p.data || []);
      setAllFilms(a.data || []);
      setRequests(r.data || []);
      setPayouts(po.data || []);
      setSpotlight(sp.data || { month: "", films: [] });
      setFilmmakers(fm.data || []);
      setFilmPerf(fp.data || []);
      setViewers(vw.data || []);
      setEventRegs(evt.data || { cap: 200, seats_taken: 0, registrations: [] });
      setSubmissions(sub.data || { count: 0, submissions: [] });
      setWatchParties(wp.data || []);
      setDonationStats(don.data || { count: 0, gross_total_naira: 0, platform_share_naira: 0, creator_share_naira: 0, recent: [] });
    } catch (err) {
      console.warn("[admin] refresh failed", err?.response?.status);
    }
  };

  useEffect(() => { refresh(); loadPosts(); }, []);

  const review = async (status) => {
    try {
      await api.post(`/admin/films/${reviewFilm.id}/review`, { status, admin_note: adminNote || null });
      toast.success(`Film ${status}`);
      setReviewFilm(null);
      setAdminNote("");
      setAiScore(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runAiScore = async () => {
    if (!reviewFilm) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/admin/films/${reviewFilm.id}/ai-score`);
      setAiScore(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI score failed");
    } finally {
      setAiLoading(false);
    }
  };

  const setSpotlightRank = async (filmId, rank) => {
    try {
      await api.post(`/admin/films/${filmId}/spotlight`, { rank });
      toast.success(`Spotlighted at rank ${rank}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeSpotlight = async (filmId) => {
    try {
      await api.delete(`/admin/films/${filmId}/spotlight`);
      toast.success("Removed from spotlight");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Set a film's publishing state. For ``coming_soon`` we also prompt
  // for a release date — kept native (``prompt()``) rather than wiring a
  // full dialog since this is an admin-only flow and the UX is terse.
  const setPublishingState = async (filmId, state) => {
    try {
      const body = { state };
      if (state === "coming_soon") {
        const existing = allFilms.find((x) => x.id === filmId)?.release_date;
        const def = existing ? existing.slice(0, 10) : "";
        const picked = window.prompt("Release date (YYYY-MM-DD):", def);
        if (!picked) return;
        body.release_date = picked;
      }
      const { data: res } = await api.post(`/admin/films/${filmId}/publishing-state`, body);
      setAllFilms((prev) => prev.map((f) => (f.id === filmId ? { ...f, publishing_state: res.publishing_state, release_date: res.release_date, premiering: res.publishing_state === "premiering" } : f)));
      toast.success(`Set to ${state.replace("_", " ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Admin-only Premiering toggle. Hits a dedicated endpoint that only
  // flips the flag — price, status, and all other film fields are
  // untouched. Local state is patched optimistically so the toggle
  // reflects immediately without waiting on a full refresh round-trip.
  const togglePremiering = async (filmId, next) => {
    try {
      await api.post(`/admin/films/${filmId}/premiering`, { premiering: next });
      setAllFilms((prev) => prev.map((f) => (f.id === filmId ? { ...f, premiering: next } : f)));
      toast.success(next ? "Marked as Premiering" : "Unmarked Premiering");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fixVideoPlayback = async (filmId) => {
    const t = toast.loading("Optimising video for browser playback…");
    try {
      const { data } = await api.post(`/admin/films/${filmId}/faststart`);
      toast.dismiss(t);
      toast.success(data.message || "Video optimised");
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not optimise video");
    }
  };

  const checkCodec = async (filmId) => {
    const t = toast.loading("Inspecting video codec…");
    try {
      const { data } = await api.get(`/admin/films/${filmId}/codec`);
      toast.dismiss(t);
      if (data.browser_friendly) {
        toast.success(data.message);
      } else {
        toast.error(data.message, { duration: 12000 });
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Codec check failed");
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMode, setDeleteMode] = useState("soft");
  const [deleteWorking, setDeleteWorking] = useState(false);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const hard = deleteMode === "hard";
    setDeleteWorking(true);
    try {
      await api.delete(`/admin/films/${deleteTarget.id}?hard=${hard}`);
      toast.success(hard ? `"${deleteTarget.title}" permanently deleted` : `"${deleteTarget.title}" hidden from the public`);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleteWorking(false);
    }
  };

  const restoreFilm = async (film) => {
    try {
      await api.post(`/admin/films/${film.id}/restore`);
      toast.success(`"${film.title}" is live again`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  const approvePayout = async () => {
    if (!payoutModal) return;
    setApproving(true);
    try {
      const { data } = await api.post(`/admin/payouts/${payoutModal.id}/approve`, {});
      if (data.status === "paid") {
        toast.success(`Paid ${payoutModal.filmmaker_name} — transfer successful`);
        setPayoutModal(null);
      } else if (data.status === "otp_required") {
        toast.message("OTP required", { description: "Paystack sent an OTP to the merchant email/phone. Enter it below." });
        setPayoutModal(data.payout);
      } else {
        toast.message(`Transfer ${data.status}`, { description: "Transfer queued with Paystack; waiting for webhook." });
        setPayoutModal(null);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer failed");
    } finally {
      setApproving(false);
    }
  };

  const finalizeOtp = async () => {
    if (!payoutModal || !otpValue) return;
    setOtpSubmitting(true);
    try {
      const { data } = await api.post(`/admin/payouts/${payoutModal.id}/finalize-otp`, { otp: otpValue });
      if (data.status === "paid") {
        toast.success("Transfer finalized — filmmaker credited");
        setPayoutModal(null);
        setOtpValue("");
      } else {
        toast.message(`Status: ${data.status}`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "OTP invalid");
    } finally {
      setOtpSubmitting(false);
    }
  };

  const rejectPayout = async (po) => {
    if (!window.confirm(`Reject ${po.filmmaker_name}'s payout of ${naira(po.amount_naira)}?`)) return;
    try {
      await api.post(`/admin/payouts/${po.id}/reject`, { admin_note: "Rejected" });
      toast.success("Payout rejected");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const [resettingViews, setResettingViews] = useState(false);
  const [deletingFm, setDeletingFm] = useState(null); // filmmaker object pending confirmation
  const [deleteFmConfirm, setDeleteFmConfirm] = useState("");
  const [deleteFmLoading, setDeleteFmLoading] = useState(false);

  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeScope, setPurgeScope] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purgeLoading, setPurgeLoading] = useState(false);

  // Inline video-replacement state — keyed by film id so multiple swaps don't
  // collide on screen. Holds the currently-uploading row + its progress %.
  const [replacingFilmId, setReplacingFilmId] = useState(null);
  const [replaceProgress, setReplaceProgress] = useState(0);

  // Same idea for poster swaps — separate state so a poster upload doesn't
  // block or step on a concurrent video replace (unusual but possible).
  const [replacingPosterId, setReplacingPosterId] = useState(null);

  // Build a cache-busted poster URL. Uses ``poster_updated_at`` when the
  // poster has been replaced, falling back to ``created_at`` for original
  // uploads. The ``?v=`` param plus the ETag/must-revalidate response
  // headers guarantee the browser + CDN fetch the fresh image immediately
  // after an admin swaps the poster.
  const posterUrl = (f) => {
    const raw = (f?.poster_updated_at || f?.created_at || "").replace(/[^a-zA-Z0-9]/g, "");
    const v = raw || f?.id || "";
    return `${API}/films/${f.id}/poster${v ? `?v=${v}` : ""}`;
  };

  // About-page image overrides — admin uploads a custom photo per slot.
  const [aboutImages, setAboutImages] = useState({});
  const [aboutUploadingSlot, setAboutUploadingSlot] = useState(null);
  useEffect(() => {
    api.get("/site/about-images").then((r) => setAboutImages(r.data || {})).catch(() => {});
  }, []);
  const uploadAboutImage = async (slot, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, or WebP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8 MB or smaller.");
      return;
    }
    setAboutUploadingSlot(slot);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await api.post(`/admin/site/about-images/${slot}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAboutImages((p) => ({ ...p, [slot]: data.url }));
      toast.success(`About-page image (${slot}) updated.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setAboutUploadingSlot(null);
    }
  };
  const clearAboutImage = async (slot) => {
    if (!window.confirm(`Reset ${slot} to the default image?`)) return;
    try {
      await api.delete(`/admin/site/about-images/${slot}`);
      setAboutImages((p) => ({ ...p, [slot]: null }));
      toast.success("Reverted to default.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    }
  };

  // Team roster — add/edit/remove members shown on the public About page.
  // All text edits auto-save via /admin/site/team/{id} PATCH when the admin
  // leaves a field; photo uploads use a separate multipart endpoint.
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamUploadingId, setTeamUploadingId] = useState(null);
  const [teamSavingId, setTeamSavingId] = useState(null);
  const [teamCreating, setTeamCreating] = useState(false);
  useEffect(() => {
    api.get("/site/team").then((r) => setTeamMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const patchTeamLocal = (id, patch) => {
    setTeamMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };
  const saveTeamField = async (id, field, value) => {
    setTeamSavingId(id);
    try {
      const { data } = await api.patch(`/admin/site/team/${id}`, { [field]: value });
      patchTeamLocal(id, data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setTeamSavingId(null);
    }
  };
  const addTeamMember = async () => {
    setTeamCreating(true);
    try {
      const { data } = await api.post(`/admin/site/team`, {
        name: `Team member ${String(teamMembers.length + 1).padStart(2, "0")}`,
        role: "Role / title",
        bio: "Add a short bio for this team member from the admin dashboard.",
      });
      setTeamMembers((prev) => [...prev, data]);
      toast.success("Team member added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setTeamCreating(false);
    }
  };
  const removeTeamMember = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this team member"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/site/team/${id}`);
      setTeamMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success("Team member removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };
  const uploadTeamPhoto = async (id, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, or WebP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8 MB or smaller.");
      return;
    }
    setTeamUploadingId(id);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await api.post(`/admin/site/team/${id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patchTeamLocal(id, { photo_url: data.url });
      toast.success("Photo updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setTeamUploadingId(null);
    }
  };
  const clearTeamPhoto = async (id) => {
    if (!window.confirm("Remove this photo? The card will fall back to initials.")) return;
    try {
      await api.delete(`/admin/site/team/${id}/photo`);
      patchTeamLocal(id, { photo_url: null });
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    }
  };

  const replaceVideoFile = async (film, file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".mp4") && !lower.endsWith(".m4v")) {
      toast.error("Video must be MP4 (H.264). Re-encode using HandBrake if needed.");
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — max 500 MB.`);
      return;
    }
    if (!window.confirm(
      `Replace the video file for "${film.title}"?\n\nThe current video will be deleted from storage and the new one streamed in its place. Viewers who already rented will see the new version.`
    )) return;
    setReplacingFilmId(film.id);
    setReplaceProgress(0);
    const fd = new FormData();
    fd.append("video", file);
    try {
      await api.post(`/admin/films/${film.id}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setReplaceProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success(`Video for "${film.title}" replaced.`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Replace failed");
    } finally {
      setReplacingFilmId(null);
      setReplaceProgress(0);
    }
  };

  const replacePosterFile = async (film, file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (![".jpg", ".jpeg", ".png", ".webp"].some((ext) => lower.endsWith(ext))) {
      toast.error("Poster must be a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`Poster is ${(file.size / 1024 / 1024).toFixed(1)} MB — max 8 MB.`);
      return;
    }
    if (!window.confirm(
      `Replace the poster for "${film.title}"?\n\nThe current poster will be replaced everywhere it's displayed.`
    )) return;
    setReplacingPosterId(film.id);
    const fd = new FormData();
    fd.append("poster", file);
    try {
      await api.post(`/admin/films/${film.id}/poster`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Poster for "${film.title}" replaced.`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Poster replace failed");
    } finally {
      setReplacingPosterId(null);
    }
  };

  const openPurgeDialog = async () => {
    setPurgeOpen(true);
    setPurgeScope(null);
    setPurgeConfirm("");
    try {
      const { data } = await api.get("/admin/purge-test-data/preview");
      setPurgeScope(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load purge scope");
      setPurgeOpen(false);
    }
  };

  const runPurge = async () => {
    if (purgeConfirm !== "PURGE") return;
    setPurgeLoading(true);
    try {
      const { data } = await api.post("/admin/purge-test-data");
      if (data.users_deleted === 0) {
        toast.info("Nothing to purge — no @test.com accounts found.");
      } else {
        const sum = Object.values(data.deleted || {}).reduce((a, b) => a + (b || 0), 0);
        toast.success(`Purged ${data.users_deleted} test user(s) · ${sum} associated records · ${data.storage_objects_purged} storage blob(s)`);
      }
      setPurgeOpen(false);
      setPurgeConfirm("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Purge failed");
    } finally {
      setPurgeLoading(false);
    }
  };

  const deleteFilmmaker = async () => {
    if (!deletingFm) return;
    setDeleteFmLoading(true);
    try {
      const { data } = await api.delete(`/admin/filmmakers/${deletingFm.id}`);
      toast.success(
        `Filmmaker deleted · ${data.films_soft_removed} film(s) hidden from catalogue (existing rentals keep playing)`
      );
      setDeletingFm(null);
      setDeleteFmConfirm("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleteFmLoading(false);
    }
  };

  const resetViewsToRentals = async () => {
    if (!window.confirm("Reset every film's view count to its number of paid rentals? This is a one-time backfill — safe to re-run, but only needed once after deploying.")) return;
    setResettingViews(true);
    try {
      const { data } = await api.post("/admin/films/reset-views-to-purchases");
      toast.success(`Reset complete · ${data.films_updated}/${data.films_scanned} films updated`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResettingViews(false);
    }
  };

  const updateRequestStatus = async (reqId, status) => {
    try {
      const { data } = await api.post(`/admin/festival-requests/${reqId}/status`, { status });
      if (data && data.invoice) {
        toast.success(`Approved — ₦${data.invoice.amount_naira.toLocaleString()} invoice sent to filmmaker`);
      } else {
        toast.success(`Request ${status.replace("_", " ")}`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const STATUS_OPTIONS = [
    { value: "received", label: "Received" },
    { value: "in_progress", label: "In progress" },
    { value: "approved", label: "Approved · invoice ₦10k" },
    { value: "submitted", label: "Submitted (requires paid invoice)" },
    { value: "completed", label: "Completed" },
    { value: "rejected", label: "Rejected" },
  ];

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-[1600px] mx-auto pb-20" data-testid="admin-dashboard">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black">Curation desk</h1>
          </div>
          <Button
            onClick={openPurgeDialog}
            variant="outline"
            size="sm"
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9"
            data-testid="purge-test-data-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Purge test data
          </Button>
        </div>

        <Tabs value={section} onValueChange={setSection} orientation="vertical" className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6 lg:gap-10">
          <AdminSidebar counts={{ pending: pending.length, payouts: payouts.filter((p) => p.status === "pending").length, festivals: requests.length }} />
          <div className="min-w-0">
          <TabsContent value="overview" className="mt-0 space-y-10" data-testid="overview-tab-content">
            <RevenueSummary revenue={overview?.revenue} />
            <section>
              <SectionHeading eyebrow="At a glance" title="Platform activity" />
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <MetricCard label="Total films" value={(stats?.total_films ?? 0).toLocaleString()} icon={Film} testId="stat-total-films" />
                <MetricCard label="Pending review" value={(stats?.pending_films ?? 0).toLocaleString()} icon={Film} tone="text-gold" testId="stat-pending-films" />
                <MetricCard label="Viewers" value={(stats?.viewers ?? 0).toLocaleString()} icon={Users} testId="stat-viewers" />
                <MetricCard label="Filmmakers" value={(stats?.filmmakers ?? 0).toLocaleString()} icon={Users} testId="stat-filmmakers" />
                <MetricCard label="Paid rentals" value={(stats?.purchases ?? 0).toLocaleString()} icon={ShoppingBag} testId="stat-rentals" />
              </div>
            </section>
          </TabsContent>

          <TabsContent value="films" className="mt-0" data-testid="films-tab-content">
            <FilmManagement films={overview?.films || []} onChanged={refresh} />
          </TabsContent>

          <TabsContent value="filmmaker-overview" className="mt-0" data-testid="filmmaker-overview-tab-content">
            <FilmmakerOverview data={overview?.filmmakers} />
          </TabsContent>

          <TabsContent value="donations" className="mt-0" data-testid="donations-tab-content">
            <DonationsOverview data={overview?.donations} />
          </TabsContent>

          <TabsContent value="pending" className="mt-0">
            {pending.length === 0 ? (
              <p className="text-foreground/50">No films awaiting review.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pending.map((f) => (
                  <div key={f.id} className="rounded-xl bg-surface border border-white/10 overflow-hidden" data-testid={`pending-film-${f.id}`}>
                    <TrailerPreview film={f} posterSrc={posterUrl(f)} className="group">
                      <label
                        className={`absolute bottom-3 right-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold cursor-pointer transition-colors shadow-lg ${
                          replacingPosterId === f.id
                            ? "bg-gold/90 text-ink"
                            : "bg-black/70 backdrop-blur text-white hover:bg-black/90 border border-white/20"
                        }`}
                        title="Replace the poster image for this film"
                        data-testid={`edit-poster-pending-${f.id}`}
                      >
                        {replacingPosterId === f.id ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <ImageIcon className="w-3.5 h-3.5" /> Edit poster
                          </>
                        )}
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          hidden
                          disabled={!!replacingPosterId}
                          onChange={(e) => replacePosterFile(f, e.target.files?.[0])}
                        />
                      </label>
                    </TrailerPreview>
                    <div className="p-5">
                      <div className="flex gap-3 mb-3">
                        <img src={posterUrl(f)} alt="" className="w-14 aspect-[2/3] rounded-md object-cover border border-white/10 shrink-0 bg-ink" onError={(e) => { e.currentTarget.style.opacity = 0; }} data-testid={`pending-poster-${f.id}`} />
                        <div className="min-w-0">
                          <h3 className="font-display font-bold text-lg mb-1">{f.title}</h3>
                          <p className="text-xs text-foreground/60">{f.filmmaker_name} · {f.genre} · {f.duration_minutes}m</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground/80 line-clamp-3 mb-4">{f.synopsis}</p>
                      <div className="flex gap-2">
                        <Link to={`/film/${f.id}`} className="flex-1">
                          <Button variant="outline" className="w-full border-white/10" size="sm">Details</Button>
                        </Link>
                        <Link to={`/watch/${f.id}`} className="flex-1">
                          <Button variant="outline" className="w-full border-gold/30 text-gold hover:bg-gold/10" size="sm" data-testid={`screen-film-${f.id}`}>
                            <Play className="w-3.5 h-3.5" /> Screen
                          </Button>
                        </Link>
                        <Button
                          onClick={() => { setReviewFilm(f); setAdminNote(""); }}
                          size="sm"
                          className="bg-terracotta hover:bg-ember text-white"
                          data-testid={`review-film-${f.id}`}
                        >Review</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-0">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-foreground/70">
                  Edit any film, swap its video, or upload new content on behalf of a filmmaker.
                </p>
              </div>
              <Link to="/admin/upload">
                <Button className="bg-terracotta hover:bg-ember text-white h-10" data-testid="admin-upload-film-btn">
                  <Upload className="w-4 h-4" /> Upload film
                </Button>
              </Link>
            </div>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-foreground/60 text-xs uppercase">
                  <tr>
                    <th className="p-3">Poster</th>
                    <th className="p-3">Title</th>
                    <th className="p-3">Filmmaker</th>
                    <th className="p-3">Genre</th>
                    <th className="p-3">Price</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Paid views</th>
                    <th className="p-3">Earnings</th>
                    <th className="p-3">Spotlight</th>
                    <th className="p-3">Publishing</th>
                    <th className="p-3">Screen</th>
                  </tr>
                </thead>
                <tbody>
                  {allFilms.map((f) => (
                    <tr key={f.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-3">
                        <div className="w-12 h-16 rounded overflow-hidden bg-ink border border-white/10">
                          <img
                            src={posterUrl(f)}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                            data-testid={`poster-thumb-${f.id}`}
                          />
                        </div>
                      </td>
                      <td className="p-3 font-medium">{f.title}</td>
                      <td className="p-3 text-foreground/70">{f.filmmaker_name}</td>
                      <td className="p-3 capitalize text-foreground/70">{f.genre}</td>
                      <td className="p-3 text-gold">{naira(f.price_naira)}</td>
                      <td className="p-3 capitalize"><Badge className="bg-white/5 border border-white/10">{f.status}</Badge></td>
                      <td className="p-3">{f.views || 0}</td>
                      <td className="p-3 text-gold">{naira(f.earnings || 0)}</td>
                      <td className="p-3">
                        {f.status === "approved" ? (
                          <Select
                            value={f.spotlight_rank ? String(f.spotlight_rank) : "0"}
                            onValueChange={(v) => v === "0" ? removeSpotlight(f.id) : setSpotlightRank(f.id, Number(v))}
                          >
                            <SelectTrigger className="bg-ink border-white/10 h-8 w-24 text-xs" data-testid={`spotlight-select-${f.id}`}>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-white/10">
                              <SelectItem value="0">— None</SelectItem>
                              <SelectItem value="1">★ #1</SelectItem>
                              <SelectItem value="2">★ #2</SelectItem>
                              <SelectItem value="3">★ #3</SelectItem>
                              <SelectItem value="4">★ #4</SelectItem>
                              <SelectItem value="5">★ #5</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : <span className="text-foreground/30 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        {f.status === "approved" ? (
                          <div className="flex flex-col gap-1">
                            <Select
                              value={f.publishing_state || "ready"}
                              onValueChange={(v) => setPublishingState(f.id, v)}
                            >
                              <SelectTrigger className="bg-ink border-white/10 h-8 w-36 text-xs" data-testid={`publishing-state-${f.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-surface border-white/10">
                                <SelectItem value="ready">⏳ Ready for Publishing</SelectItem>
                                <SelectItem value="public">🎬 Public</SelectItem>
                                <SelectItem value="coming_soon">📅 Coming Soon</SelectItem>
                                <SelectItem value="premiering">✨ Premiering</SelectItem>
                                <SelectItem value="festival_run">🏆 Festival Run</SelectItem>
                              </SelectContent>
                            </Select>
                            {f.publishing_state === "coming_soon" && f.release_date && (
                              <span className="text-[10px] text-foreground/50">{String(f.release_date).slice(0, 10)}</span>
                            )}
                          </div>
                        ) : <span className="text-foreground/30 text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 flex-wrap">
                          <Link to={`/watch/${f.id}`}>
                            <Button variant="outline" size="sm" className="border-gold/30 text-gold hover:bg-gold/10 h-8" data-testid={`screen-row-${f.id}`}>
                              <Play className="w-3.5 h-3.5" /> Watch
                            </Button>
                          </Link>
                          <Link to={`/filmmaker/edit/${f.id}`}>
                            <Button variant="outline" size="sm" className="border-terracotta/40 text-terracotta hover:bg-terracotta/10 h-8" data-testid={`edit-row-${f.id}`}>
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </Button>
                          </Link>
                          <label
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                              replacingFilmId === f.id
                                ? "border border-gold/50 bg-gold/15 text-gold"
                                : "border border-white/10 text-foreground/70 hover:text-foreground hover:bg-white/5"
                            }`}
                            title="Replace the main video file for this film. Admin only — filmmakers cannot do this post-approval."
                            data-testid={`replace-video-row-${f.id}`}
                          >
                            {replacingFilmId === f.id ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Replacing {replaceProgress}%
                              </>
                            ) : (
                              <>
                                <Upload className="w-3.5 h-3.5" /> Replace video
                              </>
                            )}
                            <input
                              type="file"
                              accept=".mp4,.m4v,video/mp4"
                              hidden
                              disabled={!!replacingFilmId}
                              onChange={(e) => replaceVideoFile(f, e.target.files?.[0])}
                            />
                          </label>
                          <label
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                              replacingPosterId === f.id
                                ? "border border-gold/50 bg-gold/15 text-gold"
                                : "border border-white/10 text-foreground/70 hover:text-foreground hover:bg-white/5"
                            }`}
                            title="Replace the poster image for this film."
                            data-testid={`replace-poster-row-${f.id}`}
                          >
                            {replacingPosterId === f.id ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Uploading…
                              </>
                            ) : (
                              <>
                                <ImageIcon className="w-3.5 h-3.5" /> Edit poster
                              </>
                            )}
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                              hidden
                              disabled={!!replacingPosterId}
                              onChange={(e) => replacePosterFile(f, e.target.files?.[0])}
                            />
                          </label>
                          <Button
                            onClick={() => checkCodec(f.id)}
                            variant="outline"
                            size="sm"
                            className="border-white/10 text-foreground/70 hover:text-foreground h-8"
                            title="Detect the video codec — flags HEVC/H.265 and other browser-incompatible formats"
                            data-testid={`codec-row-${f.id}`}
                          >
                            <Film className="w-3.5 h-3.5" /> Codec
                          </Button>
                          <Button
                            onClick={() => fixVideoPlayback(f.id)}
                            variant="outline"
                            size="sm"
                            className="border-white/10 text-foreground/70 hover:text-foreground h-8"
                            title="Re-optimize this video for browser playback (moves moov atom to the start)"
                            data-testid={`faststart-row-${f.id}`}
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Fix playback
                          </Button>
                          {f.status === "removed" ? (
                            <Button
                              onClick={() => restoreFilm(f)}
                              variant="outline"
                              size="sm"
                              className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 h-8"
                              data-testid={`restore-row-${f.id}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> Restore
                            </Button>
                          ) : (
                            <Button
                              onClick={() => { setDeleteTarget(f); setDeleteMode("soft"); }}
                              variant="outline"
                              size="sm"
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8"
                              data-testid={`delete-row-${f.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="spotlight" className="mt-0">
            <div className="rounded-xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-6 mb-4">
              <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-2 flex items-center gap-1.5">
                <Star className="w-3 h-3" /> Monthly spotlight · {spotlight.month || "—"}
              </p>
              <p className="text-sm text-foreground/70">
                Top 5 films of the month. Set rankings from the "All films" tab. Spotlighted films appear on the landing page hero strip and earn recognition + small prizes.
              </p>
              {spotlight.auto_selected && (
                <p className="mt-3 text-xs px-3 py-2 rounded-md bg-terracotta/10 border border-terracotta/30 text-terracotta" data-testid="spotlight-auto-banner">
                  <strong>Auto-fallback active:</strong> you haven't curated this month's spotlight yet, so the landing page is showing the 5 most-recent approved films. Pick #1–#5 from "All films" to override.
                </p>
              )}
            </div>
            {spotlight.films.length === 0 ? (
              <p className="text-foreground/50 text-sm">No spotlight selections yet for {spotlight.month}. Pick #1 through #5 from the All films tab.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {spotlight.films.map((f) => (
                  <div key={f.id} className={`p-5 rounded-xl bg-surface border ${f.auto_selected ? "border-white/10 opacity-90" : "border-gold/30"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`font-display text-3xl font-black ${f.auto_selected ? "text-foreground/70" : "text-gold"}`}>#{f.spotlight_rank}</p>
                      {f.auto_selected && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-terracotta/15 border border-terracotta/30 text-terracotta">Auto</span>
                      )}
                    </div>
                    <h3 className="font-display font-bold text-lg">{f.title}</h3>
                    <p className="text-xs text-foreground/60">{f.filmmaker_name} · {f.genre}</p>
                    {!f.auto_selected && (
                      <Button
                        onClick={() => removeSpotlight(f.id)}
                        variant="outline"
                        size="sm"
                        className="mt-3 border-white/10 text-foreground/70 hover:text-foreground"
                        data-testid={`remove-spotlight-${f.id}`}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="filmmakers" className="mt-0" data-testid="filmmakers-tab-content">
            {filmmakers.length === 0 ? (
              <p className="text-foreground/50">No filmmakers registered yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-foreground/60 text-xs uppercase">
                    <tr>
                      <th className="p-3">Filmmaker</th>
                      <th className="p-3">Phone</th>
                      <th className="p-3">Films</th>
                      <th className="p-3">Paid views</th>
                      <th className="p-3">Rentals</th>
                      <th className="p-3">Gross</th>
                      <th className="p-3">Earnings (70%)</th>
                      <th className="p-3">Wallet</th>
                      <th className="p-3">Joined</th>
                      <th className="p-3">Profile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filmmakers.map((fm) => (
                      <tr key={fm.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`filmmaker-row-${fm.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-terracotta flex items-center justify-center text-white text-xs font-semibold shrink-0">
                              {fm.has_avatar ? (
                                <img src={`${API}/users/${fm.id}/avatar`} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              ) : (
                                (fm.name || "?")[0].toUpperCase()
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{fm.name}</p>
                              <p className="text-[11px] text-foreground/50 truncate">{fm.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-xs" data-testid={`filmmaker-phone-${fm.id}`}>
                          {fm.phone_number ? (
                            <a
                              href={`tel:${fm.phone_number}`}
                              className="text-foreground/85 hover:text-terracotta tabular-nums"
                            >
                              {fm.phone_number}
                            </a>
                          ) : (
                            <span className="text-foreground/35">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-foreground/85 font-semibold">{fm.films_total}</span>
                          <span className="text-foreground/50 text-xs ml-1.5">
                            ({fm.films_approved}✓ · {fm.films_pending}⏳ · {fm.films_rejected}✗)
                          </span>
                        </td>
                        <td className="p-3">{fm.total_views.toLocaleString()}</td>
                        <td className="p-3">{fm.total_purchases.toLocaleString()}</td>
                        <td className="p-3 text-gold font-semibold">{naira(fm.gross_revenue_naira)}</td>
                        <td className="p-3 text-emerald-400">{naira(fm.creator_share_naira)}</td>
                        <td className="p-3 text-foreground/70">{naira(fm.wallet_balance_naira)}</td>
                        <td className="p-3 text-xs text-foreground/55">{fm.joined_at ? new Date(fm.joined_at).toLocaleDateString() : "—"}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Link to={`/@${fm.handle}`}>
                              <Button variant="outline" size="sm" className="border-white/15 h-8 text-xs">View</Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setDeletingFm(fm); setDeleteFmConfirm(""); }}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 px-2"
                              aria-label={`Delete ${fm.name}`}
                              data-testid={`delete-filmmaker-${fm.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="geo" className="mt-0" data-testid="geo-tab-content">
            {section === "geo" && <GeoInsights />}
          </TabsContent>
          <TabsContent value="broadcast" className="mt-0" data-testid="broadcast-tab-content">
            {section === "broadcast" && <BroadcastEmail />}
          </TabsContent>
          <TabsContent value="viewers" className="mt-0" data-testid="viewers-tab-content">
            <ViewerAnalytics data={overview?.viewers} />
            <h3 className="font-display text-xl font-bold mt-10 mb-4">All viewers</h3>
            {viewers.length === 0 ? (
              <p className="text-foreground/50">No viewers signed up yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-foreground/60 text-xs uppercase">
                    <tr>
                      <th className="p-3">Viewer</th>
                      <th className="p-3">Rentals</th>
                      <th className="p-3">Lifetime spend</th>
                      <th className="p-3">Wallet</th>
                      <th className="p-3">Last rented</th>
                      <th className="p-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewers.map((v) => (
                      <tr key={v.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`viewer-row-${v.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-emerald-700/60 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                              {v.has_avatar ? (
                                <img src={`${API}/users/${v.id}/avatar`} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              ) : (
                                (v.name || "?")[0].toUpperCase()
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{v.name}</p>
                              <p className="text-[11px] text-foreground/50 truncate">{v.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-foreground/85">{(v.rentals || 0).toLocaleString()}</td>
                        <td className="p-3 text-gold font-semibold">{naira(v.lifetime_spend_naira)}</td>
                        <td className={`p-3 ${v.wallet_balance_naira > 0 ? "text-emerald-400" : "text-foreground/55"}`}>
                          {naira(v.wallet_balance_naira)}
                        </td>
                        <td className="p-3 text-xs text-foreground/55">
                          {v.last_rental_at ? new Date(v.last_rental_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="p-3 text-xs text-foreground/55">
                          {v.joined_at ? new Date(v.joined_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="journal" className="mt-0 space-y-6" data-testid="journal-tab-content">
            <form onSubmit={submitPost} className="rounded-2xl border border-white/10 bg-surface p-5 sm:p-6 space-y-4" data-testid="journal-form">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-display text-lg font-bold">
                  {postEditing ? `Editing · ${postForm.title || "untitled"}` : "New journal post"}
                </h3>
                {postEditing && (
                  <Button type="button" variant="ghost" size="sm" onClick={resetPostForm} className="text-foreground/55 hover:bg-white/5" data-testid="journal-cancel-edit">
                    <XCircle className="w-4 h-4" /> Cancel edit
                  </Button>
                )}
              </div>
              <div>
                <Label className="text-xs text-foreground/65">Title</Label>
                <Input
                  value={postForm.title}
                  onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                  placeholder="A short, sharp headline"
                  className="mt-1.5 bg-ink border-white/10 h-11"
                  data-testid="journal-title-input"
                />
              </div>
              <div>
                <Label className="text-xs text-foreground/65">Excerpt (optional)</Label>
                <Input
                  value={postForm.excerpt}
                  onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                  placeholder="One-line teaser shown on the listing page"
                  className="mt-1.5 bg-ink border-white/10 h-11"
                  data-testid="journal-excerpt-input"
                />
              </div>
              <div>
                <Label className="text-xs text-foreground/65">Body</Label>
                <textarea
                  value={postForm.body}
                  onChange={(e) => setPostForm({ ...postForm, body: e.target.value })}
                  rows={10}
                  placeholder="Markdown-friendly plain text. Blank lines = paragraphs."
                  className="mt-1.5 w-full bg-ink border border-white/10 rounded-md px-3 py-2 text-sm font-mono leading-relaxed"
                  data-testid="journal-body-input"
                />
              </div>
              <div>
                <Label className="text-xs text-foreground/65">Cover image (optional, ≤ 6 MB)</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPostImage(e.target.files?.[0] || null)}
                  className="mt-1.5 block text-xs text-foreground/70 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-white/10 file:text-foreground hover:file:bg-white/15"
                  data-testid="journal-image-input"
                />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm text-foreground/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={postForm.publish_now}
                    onChange={(e) => setPostForm({ ...postForm, publish_now: e.target.checked })}
                    className="accent-terracotta"
                    data-testid="journal-publish-toggle"
                  />
                  Publish immediately
                </label>
                <label
                  className={`inline-flex items-center gap-2 text-sm cursor-pointer transition-colors ${
                    postForm.publish_now ? "text-foreground/80" : "text-foreground/40"
                  }`}
                  title={postForm.publish_now ? "" : "Only available when publishing immediately"}
                >
                  <input
                    type="checkbox"
                    checked={postForm.notify_subscribers && postForm.publish_now}
                    disabled={!postForm.publish_now}
                    onChange={(e) => setPostForm({ ...postForm, notify_subscribers: e.target.checked })}
                    className="accent-gold"
                    data-testid="journal-notify-toggle"
                  />
                  Email subscribers
                </label>
                <Button type="submit" disabled={postBusy} className="ml-auto bg-terracotta hover:bg-ember text-white" data-testid="journal-submit-btn">
                  {postBusy ? "Saving…" : postEditing ? "Update post" : (postForm.publish_now ? "Publish post" : "Save draft")}
                </Button>
              </div>
            </form>

            {posts.length === 0 ? (
              <p className="text-foreground/50">No posts yet — your first journal entry will show here.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-foreground/60 text-xs uppercase">
                    <tr>
                      <th className="p-3">Title</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Updated</th>
                      <th className="p-3 w-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`journal-row-${p.slug}`}>
                        <td className="p-3">
                          <p className="font-medium">{p.title}</p>
                          <p className="text-xs text-foreground/55">/{p.slug}</p>
                        </td>
                        <td className="p-3">
                          <Badge className={p.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-white/10 text-foreground/70 border-white/15"}>
                            {p.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-foreground/55">
                          {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1.5">
                            {p.status === "published" && (
                              <Link to={`/blog/${p.slug}`} target="_blank" rel="noopener">
                                <Button variant="outline" size="sm" className="border-white/15 h-8 text-xs">View</Button>
                              </Link>
                            )}
                            <Button onClick={() => startEditPost(p)} variant="outline" size="sm" className="border-white/15 h-8 text-xs" data-testid={`journal-edit-${p.slug}`}>Edit</Button>
                            <Button onClick={() => deletePost(p)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 px-2" data-testid={`journal-delete-${p.slug}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="performance" className="mt-0" data-testid="performance-tab-content">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <p className="text-xs text-foreground/55">
                One paid rental = one view. Numbers should match — if any historical films don't, hit the reset button once.
              </p>
              <Button
                onClick={resetViewsToRentals}
                disabled={resettingViews}
                variant="outline"
                size="sm"
                className="border-white/15 hover:bg-white/5 text-xs h-8"
                data-testid="reset-views-btn"
              >
                <RotateCcw className="w-3 h-3" /> {resettingViews ? "Resetting…" : "Reset views to rentals"}
              </Button>
            </div>
            {filmPerf.length === 0 ? (
              <p className="text-foreground/50">No films uploaded yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-foreground/60 text-xs uppercase">
                    <tr>
                      <th className="p-3">Film</th>
                      <th className="p-3">Filmmaker</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Price</th>
                      <th className="p-3">Paid views</th>
                      <th className="p-3">Rentals</th>
                      <th className="p-3">Conv.</th>
                      <th className="p-3">Gross</th>
                      <th className="p-3">Platform 30%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filmPerf.map((f) => (
                      <tr key={f.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`perf-row-${f.id}`}>
                        <td className="p-3 font-medium">{f.title}</td>
                        <td className="p-3 text-foreground/70">{f.filmmaker_name}</td>
                        <td className="p-3 capitalize text-foreground/70">{f.status}</td>
                        <td className="p-3 text-foreground/70">{naira(f.price_naira)}</td>
                        <td className="p-3">{f.views.toLocaleString()}</td>
                        <td className="p-3">{f.purchases.toLocaleString()}</td>
                        <td className="p-3 text-foreground/70">{f.conversion_rate}%</td>
                        <td className="p-3 text-gold font-semibold">{naira(f.gross_revenue_naira)}</td>
                        <td className="p-3 text-foreground/70">{naira(f.platform_share_naira)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="payouts" className="mt-0">
            {payouts.length === 0 ? (
              <p className="text-foreground/50">No payout requests yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-foreground/60 text-xs uppercase">
                    <tr>
                      <th className="p-3">Filmmaker</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Bank account</th>
                      <th className="p-3">Requested</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((p) => (
                      <tr key={p.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`payout-row-${p.id}`}>
                        <td className="p-3">
                          <p className="font-medium">{p.filmmaker_name}</p>
                          <p className="text-xs text-foreground/60">{p.filmmaker_email}</p>
                        </td>
                        <td className="p-3 text-gold font-display font-bold">{naira(p.amount_naira)}</td>
                        <td className="p-3 text-xs" data-testid={`payout-bank-${p.id}`}>
                          {p.account_number ? (
                            <div>
                              <p className="text-foreground/90 font-medium">{p.account_name}</p>
                              <p className="text-foreground/60">{p.bank_name} · {p.account_number}</p>
                            </div>
                          ) : (
                            <span className="text-foreground/40">—</span>
                          )}
                          {p.last_transfer_error && (
                            <p className="text-[11px] text-red-400 mt-1">⚠ {p.last_transfer_error}</p>
                          )}
                        </td>
                        <td className="p-3 text-xs text-foreground/60">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="p-3 capitalize">
                          <Badge className={
                            p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                            p.status === "rejected" || p.status === "failed" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                            p.status === "otp_required" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                            "bg-gold/15 text-gold border-gold/30"
                          }>{p.status.replace("_", " ")}</Badge>
                        </td>
                        <td className="p-3">
                          {(p.status === "pending" || p.status === "otp_required") && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => { setPayoutModal(p); setOtpValue(""); }}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`approve-payout-${p.id}`}
                              >
                                {p.status === "otp_required" ? "Enter OTP" : "Approve & send"}
                              </Button>
                              <Button onClick={() => rejectPayout(p)} size="sm" variant="outline" className="border-red-500/30 text-red-400" data-testid={`reject-payout-${p.id}`}>
                                Reject
                              </Button>
                            </div>
                          )}
                          {p.transaction_ref && <span className="text-xs text-foreground/50 block mt-1">ref: {p.transaction_ref}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="festivals" className="mt-0">
            {requests.length === 0 ? (
              <p className="text-foreground/50">No festival submission requests yet.</p>
            ) : (
              <div className="space-y-3">
                {requests.map((r) => (
                  <div key={r.id} className="p-4 rounded-lg bg-surface border border-white/10" data-testid={`festival-request-${r.id}`}>
                    <div className="flex flex-wrap justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold">{r.festival_name}</p>
                        <p className="text-xs text-foreground/60">{r.film_title} · {r.filmmaker_name} ({r.filmmaker_email})</p>
                        {r.notes && <p className="text-sm text-foreground/80 mt-2">{r.notes}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className="bg-terracotta/15 text-terracotta border border-terracotta/30 capitalize">{r.status.replace("_", " ")}</Badge>
                        <Select value={r.status} onValueChange={(v) => updateRequestStatus(r.id, v)}>
                          <SelectTrigger className="bg-ink border-white/10 h-9 w-40 text-xs" data-testid={`request-status-select-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-surface border-white/10">
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}-${r.id}`}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="event" className="mt-0 space-y-4" data-testid="event-tab-content">
            <EventOverview data={overview?.event} cap={eventRegs.cap} />
            <h3 className="font-display text-xl font-bold pt-6">Attendees</h3>
            <div className="rounded-2xl bg-surface border border-white/10 p-6 sm:p-8">
              <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Launch night</p>
              <h2 className="font-display text-2xl font-bold">Event RSVPs — Silverbird Galleria, 13 Nov 2026</h2>
              <p className="text-foreground/60 text-sm mt-2 max-w-2xl">
                Total seats claimed: <strong className="text-foreground/95">{eventRegs.seats_taken}</strong> / {eventRegs.cap}
                {" · "}
                <strong className="text-terracotta">{Math.max(0, eventRegs.cap - eventRegs.seats_taken)} left</strong>.
                Registration closes automatically at 200 seats.
              </p>
              {(eventRegs.registrations?.length ?? 0) === 0 ? (
                <p className="text-foreground/50 italic mt-6 text-sm">No RSVPs yet.</p>
              ) : (
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="event-rsvp-table">
                    <thead className="bg-ink text-left text-foreground/60 text-xs uppercase">
                      <tr>
                        <th className="p-3">Name</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Phone</th>
                        <th className="p-3 text-center">Party</th>
                        <th className="p-3">Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventRegs.registrations.map((r) => (
                        <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`rsvp-row-${r.id}`}>
                          <td className="p-3 font-medium">{r.name}</td>
                          <td className="p-3 text-foreground/70"><a href={`mailto:${r.email}`} className="hover:text-terracotta">{r.email}</a></td>
                          <td className="p-3 text-foreground/70 tabular-nums">{r.phone_number}</td>
                          <td className="p-3 text-center font-semibold">{r.guests}</td>
                          <td className="p-3 text-foreground/50 text-xs">{new Date(r.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="submissions" className="mt-0 space-y-4" data-testid="submissions-tab-content">
            <div className="rounded-2xl bg-surface border border-white/10 p-6 sm:p-8">
              <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Launch-night curation</p>
              <h2 className="font-display text-2xl font-bold">Short film submissions</h2>
              <p className="text-foreground/60 text-sm mt-2">
                Submissions close automatically on <strong className="text-foreground/95">31 October 2026</strong>.
                Total submitted: <strong className="text-foreground/95">{submissions.count || (submissions.submissions?.length ?? 0)}</strong>.
              </p>
              {(submissions.submissions?.length ?? 0) === 0 ? (
                <p className="text-foreground/50 italic mt-6 text-sm">No submissions yet.</p>
              ) : (
                <div className="mt-6 space-y-3">
                  {submissions.submissions.map((s) => (
                    <div key={s.id} className="p-4 rounded-xl bg-ink border border-white/10" data-testid={`submission-row-${s.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-bold text-base leading-tight">{s.title}</h3>
                          <p className="text-xs text-foreground/60 mt-0.5">
                            {s.director_name} · {s.country}
                          </p>
                        </div>
                        <a
                          href={s.film_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-terracotta/15 text-terracotta hover:bg-terracotta/25 text-xs font-semibold whitespace-nowrap"
                          data-testid={`submission-link-${s.id}`}
                        >
                          Open film →
                        </a>
                      </div>
                      <p className="text-sm text-foreground/75 mt-2 leading-relaxed line-clamp-4">{s.synopsis}</p>
                      <div className="flex flex-wrap gap-3 mt-3 text-xs text-foreground/50">
                        <span><a href={`mailto:${s.email}`} className="hover:text-terracotta">{s.email}</a></span>
                        <span className="tabular-nums">{s.phone_number}</span>
                        <span className="ml-auto">{new Date(s.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="watch-parties" className="mt-0 space-y-4" data-testid="watch-parties-tab-content">
            <WatchPartyOverview data={overview?.watch_parties} />
            <div className="pt-6"><WatchPartyPackagesEditor /></div>
            <h3 className="font-display text-xl font-bold pt-6">Sessions</h3>
            <div className="bg-surface border border-white/10 rounded-2xl p-6">
              <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Live &amp; historical parties</p>
              <h2 className="font-display text-2xl font-bold mb-1">Watch Parties</h2>
              <p className="text-foreground/65 text-sm mb-6">Every paid group-viewing session — expiry, guest count, host, amount, and film.</p>
              {watchParties.length === 0 ? (
                <p className="text-foreground/50 text-sm">No Watch Parties yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-foreground/50 border-b border-white/10">
                        <th className="py-3 pr-4">Film</th>
                        <th className="py-3 pr-4">Host</th>
                        <th className="py-3 pr-4">Package</th>
                        <th className="py-3 pr-4">Amount</th>
                        <th className="py-3 pr-4">Status</th>
                        <th className="py-3 pr-4">Expires</th>
                        <th className="py-3">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {watchParties.map((p) => {
                        const amount = p.currency === "USD"
                          ? `$${((p.amount_usd_cents || 0) / 100).toFixed(2)}`
                          : `₦${(p.amount_naira || 0).toLocaleString()}`;
                        const expiresText = p.expires_at ? new Date(p.expires_at).toLocaleString() : "—";
                        const isActive = p.status === "active" && p.expires_at && new Date(p.expires_at) > new Date();
                        return (
                          <tr key={p.id} className="border-b border-white/5" data-testid={`watch-party-row-${p.id}`}>
                            <td className="py-3 pr-4 font-medium">{p.film_title}</td>
                            <td className="py-3 pr-4 text-foreground/80">
                              <div>{p.host_name}</div>
                              <div className="text-xs text-foreground/50">{p.host_email}</div>
                            </td>
                            <td className="py-3 pr-4 tabular-nums" data-testid="wp-session-package">{p.viewer_limit ? `${p.package_name} · ${p.viewer_limit} viewers` : `${p.guest_count} guests`}</td>
                            <td className="py-3 pr-4 font-semibold text-gold tabular-nums">{amount}</td>
                            <td className="py-3 pr-4">
                              <Badge className={isActive ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : p.status === "pending" ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-white/10 text-foreground/60 border-white/20"}>
                                {isActive ? "Active" : p.status === "pending" ? "Pending" : "Ended"}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4 text-xs text-foreground/70">{expiresText}</td>
                            <td className="py-3 text-xs text-foreground/70">{new Date(p.created_at).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="site" className="mt-0 space-y-6" data-testid="site-tab-content">
            <div className="bg-surface border border-white/10 rounded-2xl p-6 sm:p-8">
              <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">About page · imagery</p>
              <h2 className="font-display text-2xl font-bold">Upload Black/African creator photos</h2>
              <p className="text-foreground/65 text-sm mt-1 max-w-2xl">
                Replace any of the five image slots on the public <strong>About</strong> page. Each upload is JPG/PNG/WebP, max 8 MB.
                Leave a slot empty to use the built-in stock photo. Hard refresh the About page to see changes.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {[
                  { slot: "hero", label: "Hero backdrop", desc: "Top of the About page" },
                  { slot: "frame1", label: "Frame 01 · The creator", desc: "Behind-the-camera shot" },
                  { slot: "frame2", label: "Frame 02 · The audience", desc: "Viewers watching" },
                  { slot: "frame3", label: "Frame 03 · The destination", desc: "Festival or recognition" },
                  { slot: "banner", label: "Destination banner", desc: "Wide quote backdrop" },
                ].map(({ slot, label, desc }) => {
                  const url = aboutImages[slot] ? `${API}${aboutImages[slot]}` : null;
                  const uploading = aboutUploadingSlot === slot;
                  return (
                    <div key={slot} className="rounded-xl border border-white/10 bg-ink overflow-hidden" data-testid={`about-image-card-${slot}`}>
                      <div className="aspect-[16/10] bg-gradient-to-br from-terracotta/20 to-ink relative">
                        {url ? (
                          <img src={url} alt={label} className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-foreground/40 text-xs">
                            Using built-in default
                          </div>
                        )}
                      </div>
                      <div className="p-3.5">
                        <p className="font-medium text-sm text-foreground">{label}</p>
                        <p className="text-[11px] text-foreground/55 mb-3">{desc}</p>
                        <div className="flex gap-2">
                          <label className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                            uploading ? "border border-gold/50 bg-gold/15 text-gold" : "border border-terracotta/40 text-terracotta hover:bg-terracotta/10"
                          }`}>
                            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> {url ? "Replace" : "Upload"}</>}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              hidden
                              disabled={uploading}
                              onChange={(e) => uploadAboutImage(slot, e.target.files?.[0])}
                              data-testid={`about-upload-${slot}`}
                            />
                          </label>
                          {url && (
                            <Button
                              type="button"
                              onClick={() => clearAboutImage(slot)}
                              variant="outline"
                              size="sm"
                              className="border-white/10 text-foreground/65 hover:text-foreground h-9 px-3"
                              data-testid={`about-clear-${slot}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* MEET THE TEAM — admin-managed roster of placeholder cards.
                Each row is a Team member with inline-editable name/role/bio
                plus a photo upload. All edits save on blur so there is no
                "Save" button to miss. */}
            <div className="bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="team-manager">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">About page · Meet the team</p>
                  <h2 className="font-display text-2xl font-bold">Edit team member cards</h2>
                  <p className="text-foreground/65 text-sm mt-1 max-w-2xl">
                    Every edit saves automatically when you tab or click away from a field. Add as many members as you like; empty rosters hide the section on the public page.
                  </p>
                </div>
                <Button
                  onClick={addTeamMember}
                  disabled={teamCreating}
                  className="bg-terracotta hover:bg-ember text-white"
                  data-testid="team-add-btn"
                >
                  {teamCreating ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : <><Users className="w-4 h-4" /> Add member</>}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {teamMembers.length === 0 && (
                  <div className="col-span-full rounded-xl border border-dashed border-white/15 bg-ink p-6 text-center text-sm text-foreground/55" data-testid="team-empty">
                    No team members yet. Click “Add member” to create the first card.
                  </div>
                )}
                {teamMembers.map((m) => {
                  const photoSrc = m.photo_url ? `${API}${m.photo_url}` : null;
                  const uploading = teamUploadingId === m.id;
                  const saving = teamSavingId === m.id;
                  return (
                    <div key={m.id} className="rounded-xl border border-white/10 bg-ink p-4" data-testid={`team-row-${m.id}`}>
                      <div className="flex gap-4">
                        <div className="w-28 h-32 shrink-0 rounded-lg overflow-hidden bg-gradient-to-br from-terracotta/25 to-gold/10 relative">
                          {photoSrc ? (
                            <img src={photoSrc} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-foreground/35 text-[11px] text-center px-2">
                              No photo
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <Input
                            defaultValue={m.name}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v && v !== m.name) saveTeamField(m.id, "name", v);
                            }}
                            className="bg-surface border-white/10 text-foreground font-semibold"
                            placeholder="Name"
                            maxLength={80}
                            data-testid={`team-name-input-${m.id}`}
                          />
                          <Input
                            defaultValue={m.role}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (m.role || "")) saveTeamField(m.id, "role", v);
                            }}
                            className="bg-surface border-white/10 text-foreground text-sm"
                            placeholder="Role / title"
                            maxLength={80}
                            data-testid={`team-role-input-${m.id}`}
                          />
                          <Textarea
                            defaultValue={m.bio}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (m.bio || "")) saveTeamField(m.id, "bio", v);
                            }}
                            className="bg-surface border-white/10 text-foreground text-sm min-h-[72px]"
                            placeholder="Short bio (up to 400 characters)"
                            maxLength={400}
                            data-testid={`team-bio-input-${m.id}`}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        <label className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                          uploading ? "border border-gold/50 bg-gold/15 text-gold" : "border border-terracotta/40 text-terracotta hover:bg-terracotta/10"
                        }`}>
                          {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> {photoSrc ? "Replace photo" : "Upload photo"}</>}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            hidden
                            disabled={uploading}
                            onChange={(e) => uploadTeamPhoto(m.id, e.target.files?.[0])}
                            data-testid={`team-photo-upload-${m.id}`}
                          />
                        </label>
                        {photoSrc && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => clearTeamPhoto(m.id)}
                            className="border-white/10 text-foreground/65 hover:text-foreground h-9 px-3"
                            data-testid={`team-photo-clear-${m.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Remove photo
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeTeamMember(m.id, m.name)}
                          className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 h-9 px-3 ml-auto"
                          data-testid={`team-delete-${m.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete member
                        </Button>
                        {saving && <span className="text-[11px] text-foreground/50 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>
          </div>
        </Tabs>
      </div>

      <Dialog open={!!reviewFilm} onOpenChange={(o) => !o && (setReviewFilm(null), setAiScore(null))}>
        <DialogContent className="bg-surface border-white/10 text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review: {reviewFilm?.title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {reviewFilm && (
              <div className="grid grid-cols-[96px_1fr] gap-3" data-testid="review-media">
                <img src={posterUrl(reviewFilm)} alt={reviewFilm.title} className="w-full aspect-[2/3] rounded-lg object-cover border border-white/10 bg-ink" onError={(e) => { e.currentTarget.style.opacity = 0; }} data-testid="review-poster" />
                <TrailerPreview key={reviewFilm.id} film={reviewFilm} posterSrc={posterUrl(reviewFilm)} className="rounded-lg border border-white/10" />
              </div>
            )}
            {reviewFilm && (
              <div className="rounded-lg border border-white/10 bg-ink p-3 text-xs text-foreground/70">
                <span className="text-foreground/50">By:</span> {reviewFilm.filmmaker_name} · {reviewFilm.genre} · {reviewFilm.duration_minutes}m
                <p className="mt-1 text-foreground/80 line-clamp-3">{reviewFilm.synopsis}</p>
              </div>
            )}

            <div className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-widest text-terracotta font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> AI curation pre-score
                </p>
                <Button onClick={runAiScore} disabled={aiLoading} size="sm" className="bg-terracotta hover:bg-ember text-white" data-testid="run-ai-score-btn">
                  {aiLoading ? "Scoring…" : aiScore ? "Re-score" : "Get score"}
                </Button>
              </div>
              {aiScore ? (
                <div data-testid="ai-score-panel">
                  <div className="flex items-baseline gap-3 mb-3">
                    <p className="font-display text-4xl font-black">{aiScore.total}</p>
                    <p className="text-xs text-foreground/60">/100</p>
                    <Badge className={
                      aiScore.verdict === "approve" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                      aiScore.verdict === "reject" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                      "bg-gold/15 text-gold border-gold/30"
                    } data-testid="ai-verdict">
                      {aiScore.verdict}
                    </Badge>
                  </div>
                  {aiScore.scores && (
                    <div className="grid grid-cols-5 gap-2 mb-3 text-center">
                      {Object.entries(aiScore.scores).map(([k, v]) => (
                        <div key={k} className="p-2 rounded bg-ink/60 border border-white/5">
                          <p className="font-display font-bold text-base">{v}</p>
                          <p className="text-[10px] text-foreground/50 uppercase tracking-wider">{k}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {aiScore.summary && <p className="text-sm text-foreground/85 mb-2"><strong>Summary:</strong> {aiScore.summary}</p>}
                  {aiScore.strengths && <p className="text-xs text-emerald-300 mb-1"><strong>Strengths:</strong> {aiScore.strengths}</p>}
                  {aiScore.concerns && <p className="text-xs text-red-300"><strong>Concerns:</strong> {aiScore.concerns}</p>}
                </div>
              ) : (
                <p className="text-sm text-foreground/60">Click "Get score" for an AI rubric pre-score (5 categories x 20). Final call is yours.</p>
              )}
            </div>

            <p className="text-sm text-foreground/70">Add an optional note (visible to filmmaker).</p>
            <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} className="bg-ink border-white/10" rows={3} placeholder="e.g. Great work — polished cinematography." data-testid="admin-note-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => review("rejected")} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="reject-film-btn">
              <XCircle className="w-4 h-4" /> Reject
            </Button>
            <Button onClick={() => review("approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-film-btn">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payoutModal} onOpenChange={(o) => !o && setPayoutModal(null)}>
        <DialogContent className="bg-surface border-white/10 text-foreground" data-testid="payout-approve-dialog">
          <DialogHeader>
            <DialogTitle>
              {payoutModal?.status === "otp_required" ? "Finalize transfer with OTP" : "Approve & send transfer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {payoutModal && (
              <div className="rounded-lg border border-white/10 bg-ink p-4 text-sm space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-foreground/60">Payout amount</span>
                  <span className="font-display font-bold text-gold text-lg">{naira(payoutModal.amount_naira)}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-foreground/60">Recipient</span>
                  <span className="text-foreground/90 font-medium">{payoutModal.filmmaker_name}</span>
                </div>
                <div className="pt-2 mt-2 border-t border-white/5">
                  <p className="text-[11px] uppercase tracking-widest text-foreground/50 mb-1">Crediting to</p>
                  <p className="font-display font-bold">{payoutModal.account_name || "—"}</p>
                  <p className="text-xs text-foreground/70">{payoutModal.bank_name} · {payoutModal.account_number}</p>
                </div>
                {payoutModal.last_transfer_error && (
                  <p className="text-xs text-red-400">⚠ {payoutModal.last_transfer_error}</p>
                )}
              </div>
            )}

            {payoutModal?.status === "otp_required" ? (
              <div>
                <label className="text-sm text-foreground/80">Paystack OTP (check the merchant email/SMS)</label>
                <Input
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ""))}
                  placeholder="6-digit OTP"
                  inputMode="numeric"
                  maxLength={10}
                  className="mt-1.5 bg-ink border-white/10 h-11 tracking-[0.3em] text-center text-lg"
                  data-testid="payout-otp-input"
                />
                <p className="text-xs text-foreground/55 mt-1.5">Tip: disable OTP for Transfers in Paystack → Preferences to skip this step on future payouts.</p>
              </div>
            ) : (
              <p className="text-xs text-foreground/60">
                Paystack will debit your merchant balance and credit the filmmaker's bank account. Depending on your account setup, an OTP may be required before the transfer finalizes.
              </p>
            )}
          </div>
          <DialogFooter>
            {payoutModal?.status === "otp_required" ? (
              <Button
                onClick={finalizeOtp}
                disabled={otpSubmitting || otpValue.length < 4}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-otp-btn"
              >
                <CheckCircle2 className="w-4 h-4" /> {otpSubmitting ? "Finalizing…" : "Finalize transfer"}
              </Button>
            ) : (
              <Button
                onClick={approvePayout}
                disabled={approving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-approve-payout-btn"
              >
                <DollarSign className="w-4 h-4" /> {approving ? "Sending transfer…" : "Approve & send"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-surface border-white/10 max-w-md" data-testid="delete-film-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Delete &quot;{deleteTarget?.title}&quot;?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 my-3">
            <button
              onClick={() => setDeleteMode("soft")}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${deleteMode === "soft" ? "border-terracotta bg-terracotta/10" : "border-white/10 hover:border-white/30"}`}
              data-testid="delete-mode-soft"
            >
              <p className="font-semibold text-sm flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded-full border-2 ${deleteMode === "soft" ? "border-terracotta bg-terracotta" : "border-white/30"}`} />
                Hide from public
              </p>
              <p className="text-xs text-foreground/65 mt-1.5 ml-5">
                Recommended. Removes the film from Browse, Featured, and Spotlight.
                <strong className="text-foreground/80"> Existing buyers keep lifetime access.</strong> You can restore it any time.
              </p>
            </button>
            <button
              onClick={() => setDeleteMode("hard")}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${deleteMode === "hard" ? "border-red-500 bg-red-500/10" : "border-white/10 hover:border-white/30"}`}
              data-testid="delete-mode-hard"
            >
              <p className="font-semibold text-sm flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded-full border-2 ${deleteMode === "hard" ? "border-red-500 bg-red-500" : "border-white/30"}`} />
                Delete permanently
              </p>
              <p className="text-xs text-foreground/65 mt-1.5 ml-5">
                Removes the database record and deletes the video + poster from object storage.
                <strong className="text-red-400"> This cannot be undone.</strong> Active rentals will lose access.
              </p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-white/15">Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteWorking}
              className={deleteMode === "hard"
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-terracotta hover:bg-ember text-white"}
              data-testid="delete-confirm-btn"
            >
              {deleteWorking ? "Working…" : (deleteMode === "hard" ? "Delete forever" : "Hide from public")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletingFm} onOpenChange={(o) => !o && (setDeletingFm(null), setDeleteFmConfirm(""))}>
        <DialogContent className="bg-surface border-white/10 max-w-md" data-testid="delete-filmmaker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Delete filmmaker?
            </DialogTitle>
          </DialogHeader>

          {deletingFm && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg border border-white/10 bg-ink p-4 space-y-1">
                <p className="font-display font-bold text-base">{deletingFm.name}</p>
                <p className="text-foreground/60 text-xs">{deletingFm.email}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                  <span className="text-foreground/60">Films</span>
                  <span className="text-right">{deletingFm.films_total} ({deletingFm.films_approved} live)</span>
                  <span className="text-foreground/60">Lifetime rentals</span>
                  <span className="text-right">{(deletingFm.total_purchases || 0).toLocaleString()}</span>
                  <span className="text-foreground/60">Wallet balance</span>
                  <span className={`text-right ${deletingFm.wallet_balance_naira > 0 ? "text-amber-300 font-semibold" : ""}`}>
                    {naira(deletingFm.wallet_balance_naira)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs leading-relaxed text-foreground/85">
                <p className="font-semibold text-amber-300 mb-1">What happens:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>The user account is permanently deleted.</li>
                  <li>Their {deletingFm.films_total || 0} film(s) are hidden from Browse, but viewers who already paid keep lifetime access.</li>
                  <li>Purchase + payout history is kept for accounting (NDPA / FIRS retention).</li>
                  <li>If wallet balance &gt; ₦0 or any payout is in flight, deletion will be refused.</li>
                </ul>
              </div>

              <div>
                <Label className="text-foreground/80">Type <strong className="text-red-400">DELETE</strong> to confirm</Label>
                <Input
                  value={deleteFmConfirm}
                  onChange={(e) => setDeleteFmConfirm(e.target.value)}
                  placeholder="DELETE"
                  className="mt-1.5 bg-ink border-white/10 h-10 tracking-widest"
                  data-testid="delete-filmmaker-confirm-input"
                  autoFocus
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeletingFm(null); setDeleteFmConfirm(""); }}
              className="border-white/15"
              data-testid="delete-filmmaker-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={deleteFilmmaker}
              disabled={deleteFmLoading || deleteFmConfirm !== "DELETE"}
              className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-900/40"
              data-testid="delete-filmmaker-confirm-btn"
            >
              <Trash2 className="w-4 h-4" /> {deleteFmLoading ? "Deleting…" : "Delete filmmaker"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!o) { setPurgeOpen(false); setPurgeConfirm(""); } }}>
        <DialogContent className="bg-surface border-white/10 max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Purge test data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs leading-relaxed text-foreground/85">
              <p className="font-semibold text-amber-300 mb-1.5">This will hard-delete:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Every account whose email ends in <code className="text-foreground/95 font-mono">@test.com</code></li>
                <li>All their films (videos + posters removed from storage)</li>
                <li>All purchases, payouts, ledger entries, and referral payouts that touch them</li>
                <li>Festival requests, AI analyses, and invoices tied to those films</li>
              </ul>
              <p className="mt-2 text-foreground/65">
                Pattern is locked at <code className="font-mono">@test.com</code> server-side — real accounts (admin, viewers, filmmakers on other domains) are untouched.
              </p>
            </div>

            {purgeScope === null ? (
              <p className="text-foreground/50 text-xs">Scanning database…</p>
            ) : (
              <div className="rounded-lg border border-white/10 bg-ink p-4" data-testid="purge-scope">
                <p className="text-[11px] uppercase tracking-widest text-foreground/55 mb-2">Records found</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <span className="text-foreground/65">Users (@test.com)</span>
                  <span className="text-right font-display font-bold text-red-400" data-testid="purge-scope-users">{purgeScope.users}</span>
                  <span className="text-foreground/65">Films</span>
                  <span className="text-right">{purgeScope.films}</span>
                  <span className="text-foreground/65">Purchases</span>
                  <span className="text-right">{purgeScope.purchases}</span>
                  <span className="text-foreground/65">Payouts</span>
                  <span className="text-right">{purgeScope.payouts}</span>
                  <span className="text-foreground/65">Wallet ledger</span>
                  <span className="text-right">{purgeScope.wallet_ledger}</span>
                  <span className="text-foreground/65">Wallet top-ups</span>
                  <span className="text-right">{purgeScope.wallet_topups}</span>
                  <span className="text-foreground/65">Referral payouts</span>
                  <span className="text-right">{purgeScope.referral_payouts}</span>
                  <span className="text-foreground/65">Festival records</span>
                  <span className="text-right">{purgeScope.festival_requests + purgeScope.festival_analyses + purgeScope.festival_invoices}</span>
                </div>
                {purgeScope.users === 0 && (
                  <p className="mt-3 text-foreground/60 text-xs italic">Nothing to purge — the database is already clean.</p>
                )}
              </div>
            )}

            {purgeScope && purgeScope.users > 0 && (
              <div>
                <Label className="text-foreground/80">Type <strong className="text-red-400">PURGE</strong> to confirm</Label>
                <Input
                  value={purgeConfirm}
                  onChange={(e) => setPurgeConfirm(e.target.value)}
                  placeholder="PURGE"
                  className="mt-1.5 bg-ink border-white/10 h-10 tracking-widest"
                  data-testid="purge-confirm-input"
                  autoFocus
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setPurgeOpen(false); setPurgeConfirm(""); }}
              className="border-white/15"
              data-testid="purge-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={runPurge}
              disabled={purgeLoading || !purgeScope || purgeScope.users === 0 || purgeConfirm !== "PURGE"}
              className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-900/40"
              data-testid="purge-confirm-btn"
            >
              <Trash2 className="w-4 h-4" /> {purgeLoading ? "Purging…" : `Purge ${purgeScope?.users || 0} account(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
